import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Button, Chip, Dialog, DialogContent, Divider } from '@material-ui/core';
import { ChipTextWrap, TagsDisplay } from './TagDisplay';
import { Tag } from './useTagManagement';
import { useTextFilter } from '../schemed/Filtering/useTextFilter';
import { SearchField } from '../primitives/SearchField';
import { FormattedMessage } from 'react-intl';

export interface RecordTagEditProps {
    availableTags: Tag[];
    selectedTags: Tag[];
    addTag: (t: Tag) => void;
    removeTag: (t: Tag) => void;
    addNewTag: (label: string) => void;
    clear: () => void;
    blockAddNew?: boolean;
    wrapTags?: boolean;
    noDisplaySelected?: boolean;
    stretchSearchField?: boolean;
}

interface DialogProps {
    isOpen: boolean;
    close: () => void;
}

const AvailableTagsWrapper = styled.div`
    display: flex;
    flex-flow: row wrap;
    gap: 0.5rem;
    padding: 1rem 0;
`;

const SearchAndCreateWrapper = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
`;

const Separator = styled(Divider)`
    margin: 1rem 0 0.5rem;
`;

export const RecordTagsEdit = (props: RecordTagEditProps) => {
    const {
        availableTags,
        selectedTags,
        removeTag,
        addTag,
        addNewTag,
        blockAddNew,
    } = props;

    const { filter, setFilter, filterData } = useTextFilter<Tag>(t => t.label);

    const filteredTags = filterData(availableTags);

    const canCreate = !blockAddNew && filter.length > 0 && filteredTags.length === 0;

    return (
        <>
            <TagsDisplay tags={selectedTags} remove={removeTag} wrap={props.wrapTags} />
            <Separator />
            <SearchAndCreateWrapper>
                <SearchField
                    filter={filter}
                    setFilter={setFilter}
                    noButton
                    autoFocus
                    />
                {canCreate ?
                    <Button
                        color="primary"
                        size="small"
                        onClick={() => { addNewTag(filter); setFilter(""); }}
                        >
                        <FormattedMessage id="tags.create" />
                    </Button> :
                    <div/>}
            </SearchAndCreateWrapper>
            <AvailableTagsWrapper>
                {filteredTags.map(t => {
                    const isSelected = selectedTags.find(ts => ts._id === t._id);
                    return <Chip
                        key={t._id}
                        label={t.label}
                        variant={isSelected ? "default" : "outlined"}
                        color={isSelected ? "primary" : "default"}
                        onClick={isSelected ? () => removeTag(t) : () => addTag(t)}
                        component={props.wrapTags ? ChipTextWrap : "div"}
                        />
                    })}
            </AvailableTagsWrapper>
        </>
    );
}

export const RecordTagsEditPopup = (props: RecordTagEditProps & DialogProps) => {
    const {
        isOpen,
        close,
    } = props;

    return (
        <Dialog open={isOpen} onClose={() => close()} fullWidth maxWidth="md">
            <DialogContent>
                <RecordTagsEdit {...props} />
            </DialogContent>
        </Dialog>
    );
}


export const useTagSelection = (availableTags: Tag[]): RecordTagEditProps => {
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

    return {
        addNewTag: () => {},
        blockAddNew: true,
        selectedTags,
        addTag: t => setSelectedTags(ts => [...ts, t]),
        removeTag: t => setSelectedTags(ts => ts.filter(te => te._id !== t._id)),
        clear: () => setSelectedTags([]),
        availableTags,
    }
}
